import * as React from "react";
import classNames from "classnames";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Dropdown from "react-bootstrap/Dropdown";
import Spinner from "react-bootstrap/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport, faPencil } from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import { gettext } from "i18n";
import {
  exportDashboard,
  exportWidgetAsJPEG,
  exportWidgetAsExcel,
  EXPORT_FORMATS,
  ExportFormat,
} from "utils/export";
import PageHeader from "theme/components/common/PageHeader";
import Flex from "theme/components/common/Flex";
import { useAuth, isOrgAdmin } from "state/auth";
import { useModalContext } from "state/modal";
import WidgetFormModal from "components/modals/WidgetFormModal";
import WidgetSettingsModal from "components/modals/WidgetSettingsModal";
import AddDashboardStationForm from "components/modals/AddDashboardStationForm";
import CopyWidgetToDashboardModal from "components/modals/CopyWidgetToDashboardModal";
import ControlledDateRangeSelector, {
  getDefaultDateRange,
} from "components/dates/ControlledDateRangeSelector";
import apiClient from "api";
import { getFormValueHandler } from "utils/forms";
import { GET_REPORTING_DASHBOARD_STRUCTURE } from "utils/stations";
import { GET_ORGANIZATIONS } from "utils/users";
import {
  GetReportingDashboardStructure,
  GetReportingDashboardStructureVariables,
} from "utils/__generated__/GetReportingDashboardStructure";
import {
  GetOrganizations,
  GetOrganizations_organization,
} from "utils/__generated__/GetOrganizations";
import {
  WIDGET_TEMPLATES,
  WidgetTemplate,
  VisualSettings,
  ChartVisualSettings,
  TableVisualSettings,
  SourceVisualSettings,
  DASHBOARD_TYPE_REPORTING,
} from "utils/widgets";
import { canCopyWidget, updateWidgetInStructure } from "utils/dashboards";
import ReportingDashboardStation from "../dashboardComponents/ReportingDashboardStation";
import { DragProvider } from "utils/dnd";
import DatasourceSettingsModal from "components/modals/DatasourceSettingsModal";
import "./ReportingDashboard.scss";

interface WidgetStructure {
  key: string;
  metaData: Record<string, any>;
  dateRange?: [string, string];
  visualSettings?: VisualSettings;
}

interface WidgetRowStructure {
  widgets: WidgetStructure[];
}

interface StationStructure {
  station: {
    identifier: string;
    widget_rows: WidgetRowStructure[];
  };
}

interface ReportingDashboardDetailsPageProps {
  editWidgets?: boolean;
}

const ReportingDashboardDetailsPage = ({
  editWidgets = false,
}: ReportingDashboardDetailsPageProps) => {
  const { id } = useParams();
  const { user } = useAuth();
  const { activeModal, modalPayload, setActiveModal, setModalPayload } =
    useModalContext();
  const [submitDisabled, setSubmitDisabled] = React.useState(false);
  const [changesMade, setChangesMade] = React.useState(false);
  const [renamingDashboard, setRenamingDashboard] = React.useState(false);
  const [editedStructure, setEditedStructure] = React.useState<
    StationStructure[] | null
  >(null);
  const [dashboardEditedValues, setDashboardEditedValues] = React.useState<
    Record<string, any>
  >({});
  const setValueHandler = getFormValueHandler(
    dashboardEditedValues,
    setDashboardEditedValues,
  );
  const [settingsWidget, setSettingsWidget] = React.useState<{
    widget: WidgetStructure;
    rowIndex: number;
    widgetIndex: number;
    stationIndex: number;
  } | null>(null);
  const [exportFormat, setExportFormat] = React.useState<ExportFormat>("jpeg");
  const [isExporting, setIsExporting] = React.useState(false);
  const dashboardRef = React.useRef<HTMLDivElement>(null);

  const [datasourceSettings, setDatasourceSettings] = React.useState<{
    sourceUUID: string;
    sourceName: string;
    sourceIndex: number;
    widget: WidgetStructure;
    stationIndex: number;
    rowIndex: number;
    widgetIndex: number;
  } | null>(null);

  const numericId = parseInt(id ?? "", 10);

  const { data, refetch: refetchDashboardStructure } = useQuery<
    GetReportingDashboardStructure,
    GetReportingDashboardStructureVariables
  >(GET_REPORTING_DASHBOARD_STRUCTURE, {
    variables: { id: numericId },
  });
  const { data: orgData } = useQuery<GetOrganizations>(GET_ORGANIZATIONS, {
    skip: !user?.admin,
  });

  const dashboard = data?.reporting_dashboard?.[0];

  const canEditDashboard = !!(
    user?.admin ||
    (dashboard?.organization_id && isOrgAdmin(user, dashboard.organization_id))
  );
  const canEditWidgets = editWidgets && canEditDashboard;

  React.useEffect(() => {
    if (dashboard?.structure) {
      const normalizedStructure = dashboard.structure.map(
        (station: StationStructure) => ({
          ...station,
          station: {
            ...station.station,
            widget_rows: station.station.widget_rows.map(
              (row: WidgetRowStructure) => ({
                ...row,
                widgets: row.widgets.map((widget: WidgetStructure) => ({
                  ...widget,
                  dateRange:
                    widget.dateRange ||
                    getDefaultDateRange().map((d) => d.toISOString()),
                })),
              }),
            ),
          },
        }),
      );
      setEditedStructure(normalizedStructure);
    }
  }, [dashboard?.structure]);

  const showAddStation = React.useMemo(
    () => () => {
      setActiveModal("add-dashboard-station");
    },
    [setActiveModal],
  );

  const showCopyWidget = React.useCallback(
    (
      _widget: WidgetStructure,
      stationIndex: number,
      rowIndex: number,
      widgetIndex: number,
    ) => {
      if (!canCopyWidget(changesMade)) {
        return;
      }
      setModalPayload({
        stationIdentifier: editedStructure?.[stationIndex]?.station?.identifier,
        rowIndex,
        widgetIndex,
      });
      setActiveModal("copy-dashboard-widget");
    },
    [changesMade, editedStructure, setModalPayload, setActiveModal],
  );

  const handleWidgetDateRangeChange = (
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
    newRange: [Date, Date],
  ) => {
    if (!editedStructure) return;

    const newStructure = updateWidgetInStructure(
      editedStructure,
      stationIndex,
      rowIndex,
      widgetIndex,
      (widget) => ({
        ...widget,
        dateRange: newRange.map((d) => d.toISOString()) as [string, string],
      }),
    );
    if (newStructure === editedStructure) {
      return;
    }
    setEditedStructure(newStructure);
    setChangesMade(true);
  };

  const handleWidgetVisualSettingsChange = (
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
    newSettings: VisualSettings,
  ) => {
    if (!editedStructure) return;

    const newStructure = updateWidgetInStructure(
      editedStructure,
      stationIndex,
      rowIndex,
      widgetIndex,
      (widget) => ({ ...widget, visualSettings: newSettings }),
    );
    if (newStructure === editedStructure) {
      return;
    }
    setEditedStructure(newStructure);
    setChangesMade(true);
  };

  const handleDatasourceVisualSettingsChange = (
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
    newSourceSettings: SourceVisualSettings,
  ) => {
    if (!editedStructure) return;

    const widget =
      editedStructure[stationIndex].station.widget_rows[rowIndex].widgets[
        widgetIndex
      ];
    const currentSettings = (widget.visualSettings ||
      {}) as ChartVisualSettings;
    const currentSourceSettings = currentSettings.sourceVisualSettings || [];

    const existingIndex = currentSourceSettings.findIndex(
      (s) => s.sourceUUID === newSourceSettings.sourceUUID,
    );

    let newSourceSettingsArray: SourceVisualSettings[];
    if (existingIndex >= 0) {
      newSourceSettingsArray = [...currentSourceSettings];
      newSourceSettingsArray[existingIndex] = newSourceSettings;
    } else {
      newSourceSettingsArray = [...currentSourceSettings, newSourceSettings];
    }

    const newStructure = updateWidgetInStructure(
      editedStructure,
      stationIndex,
      rowIndex,
      widgetIndex,
      (currentWidget) => ({
        ...currentWidget,
        visualSettings: {
          ...currentSettings,
          sourceVisualSettings: newSourceSettingsArray,
        },
      }),
    );
    if (newStructure === editedStructure) {
      return;
    }
    setEditedStructure(newStructure);
    setChangesMade(true);
  };

  const openDatasourceSettings = (
    sourceUUID: string,
    sourceName: string,
    sourceIndex: number,
    widget: WidgetStructure,
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => {
    setDatasourceSettings({
      sourceUUID,
      sourceName,
      sourceIndex,
      widget,
      stationIndex,
      rowIndex,
      widgetIndex,
    });
    setActiveModal("datasource-settings");
  };

  const openWidgetSettings = (
    widget: WidgetStructure,
    stationIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => {
    setSettingsWidget({ widget, stationIndex, rowIndex, widgetIndex });
    setActiveModal("widget-settings");
  };

  const toggleDashboardRename = () => {
    setRenamingDashboard(!renamingDashboard);
  };

  const cleanEditedStructure = (structure: StationStructure[]) => {
    return structure.map((stationStructure) => ({
      ...stationStructure,
      station: {
        ...stationStructure.station,
        widget_rows: stationStructure.station.widget_rows.filter(
          (row) => row.widgets.length > 0,
        ),
      },
    }));
  };

  const onSaveEditedStructure = async () => {
    if (submitDisabled || !editedStructure) {
      return;
    }
    setSubmitDisabled(true);

    try {
      await apiClient.request(
        "/organization-admin/update-reporting-dashboard-structure",
        {
          method: "POST",
          data: {
            dashboardId: numericId,
            newStructure: cleanEditedStructure(editedStructure),
            dashboardMeta: {
              ...dashboardEditedValues,
            },
          },
        },
      );
      toast.success(gettext("Reporting dashboard updated successfully"));
      setChangesMade(false);
      setDashboardEditedValues({});
      setRenamingDashboard(false);
      await refetchDashboardStructure();
    } catch (e: any) {
      console.error(e);
      toast.error(
        `${gettext("Failed to update dashboard")} ${gettext(e.toString())}`,
      );
    }
    setSubmitDisabled(false);
  };

  const onCancelEditingStructure = () => {
    if (submitDisabled) {
      return;
    }
    if (dashboard?.structure) {
      const normalizedStructure = dashboard.structure.map(
        (station: StationStructure) => ({
          ...station,
          station: {
            ...station.station,
            widget_rows: station.station.widget_rows.map(
              (row: WidgetRowStructure) => ({
                ...row,
                widgets: row.widgets.map((widget: WidgetStructure) => ({
                  ...widget,
                  dateRange:
                    widget.dateRange ||
                    getDefaultDateRange().map((d) => d.toISOString()),
                })),
              }),
            ),
          },
        }),
      );
      setEditedStructure(normalizedStructure);
    }
    setChangesMade(false);
    setDashboardEditedValues({});
    setRenamingDashboard(false);
  };

  const handleExportReport = async () => {
    if (isExporting || !dashboard) {
      return;
    }

    setIsExporting(true);

    try {
      await exportDashboard(exportFormat, dashboardRef.current, dashboard.name);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error(gettext("Failed to export report"));
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportWidget = async (
    widgetElement: HTMLElement,
    widgetTitle: string,
  ) => {
    try {
      await exportWidgetAsJPEG(widgetElement, widgetTitle);
      toast.success(gettext("Widget exported successfully"));
    } catch (error) {
      console.error("Widget export failed:", error);
      toast.error(gettext("Failed to export widget"));
    }
  };

  const handleExportWidgetExcel = async (widget: WidgetStructure) => {
    if (!dashboard?.id) {
      toast.error(gettext("Dashboard not found"));
      return;
    }
    try {
      const widgetTitle = widget.metaData?.title || widget.key;
      await exportWidgetAsExcel(
        dashboard.id,
        widget.metaData?.widgetId,
        widget.key,
        widgetTitle,
      );
      toast.success(gettext("Widget exported successfully"));
    } catch (error) {
      console.error("Widget Excel export failed:", error);
      toast.error(gettext("Failed to export widget"));
    }
  };

  if (!dashboard) {
    return (
      <div>
        <PageHeader title="Dashboard not found" className="mb-3" />
      </div>
    );
  }

  const stationsExist = (editedStructure?.length ?? 0) > 0;
  const organizationList: GetOrganizations_organization[] | undefined =
    orgData?.organization ?? [];
  const dashboardOrg = organizationList?.find(
    (org) => org.id === dashboard.organization_id,
  );

  const getWidgetTemplate = (key: string): WidgetTemplate | undefined => {
    return WIDGET_TEMPLATES.find((t) => t.key === key);
  };

  return (
    <>
      {(activeModal === "add-dashboard-widget" ||
        activeModal === "edit-dashboard-widget") && (
        <WidgetFormModal
          stationIdentifier={modalPayload.stationIdentifier}
          editedStructure={editedStructure}
          setEditedStructure={setEditedStructure}
          setChangesMade={setChangesMade}
          editingWidget={modalPayload.editingWidget}
          dashboardType={DASHBOARD_TYPE_REPORTING}
        />
      )}
      {activeModal === "add-dashboard-station" && (
        <AddDashboardStationForm
          editedStructure={editedStructure}
          setEditedStructure={setEditedStructure}
          onSaveEditedStructure={async (e) => {
            e.preventDefault();
            await onSaveEditedStructure();
          }}
        />
      )}
      {activeModal === "copy-dashboard-widget" && (
        <CopyWidgetToDashboardModal
          dashboardType={DASHBOARD_TYPE_REPORTING}
          sourceDashboardId={numericId}
          stationIdentifier={modalPayload.stationIdentifier}
          rowIndex={modalPayload.rowIndex}
          widgetIndex={modalPayload.widgetIndex}
          onCopied={(targetDashboardId) => {
            if (targetDashboardId === numericId) {
              refetchDashboardStructure();
            }
          }}
        />
      )}
      {activeModal === "widget-settings" && settingsWidget && (
        <WidgetSettingsModal
          visualSettings={settingsWidget.widget.visualSettings}
          visualSettingsType={
            getWidgetTemplate(settingsWidget.widget.key)?.visualSettingsType ||
            "none"
          }
          isMultiSource={
            getWidgetTemplate(settingsWidget.widget.key)
              ?.supportsMultipleSources
          }
          onSave={(newSettings) =>
            handleWidgetVisualSettingsChange(
              settingsWidget.stationIndex,
              settingsWidget.rowIndex,
              settingsWidget.widgetIndex,
              newSettings,
            )
          }
        />
      )}
      {activeModal === "datasource-settings" && datasourceSettings && (
        <DatasourceSettingsModal
          sourceUUID={datasourceSettings.sourceUUID}
          sourceName={datasourceSettings.sourceName}
          sourceIndex={datasourceSettings.sourceIndex}
          currentSettings={(
            datasourceSettings.widget.visualSettings as ChartVisualSettings
          )?.sourceVisualSettings?.find(
            (s) => s.sourceUUID === datasourceSettings.sourceUUID,
          )}
          onSave={(newSettings) =>
            handleDatasourceVisualSettingsChange(
              datasourceSettings.stationIndex,
              datasourceSettings.rowIndex,
              datasourceSettings.widgetIndex,
              newSettings,
            )
          }
        />
      )}

      {isExporting && (
        <div className="export-overlay">
          <div className="text-center">
            <Spinner animation="border" role="status" className="mb-2" />
            <div>{gettext("Preparing export...")}</div>
          </div>
        </div>
      )}

      <DragProvider>
        <div
          ref={dashboardRef}
          className={classNames({
            "editable-dashboard-container": canEditWidgets,
          })}
        >
          <PageHeader
            title={
              <div className="dashboard-header-title">
                <div>
                  {renamingDashboard ? (
                    <Form.Control
                      type="text"
                      value={dashboardEditedValues.name ?? dashboard.name}
                      onChange={(e) => {
                        if (!changesMade) {
                          setChangesMade(true);
                        }
                        setValueHandler("name")(e);
                      }}
                    />
                  ) : (
                    dashboard.name
                  )}
                  {canEditWidgets && user?.admin && (
                    <div
                      className="dashboard-widget-button dashboard-rename-button"
                      onClick={toggleDashboardRename}
                    >
                      <FontAwesomeIcon icon={faPencil} />
                    </div>
                  )}
                </div>
                {canEditWidgets && user?.admin && (
                  <div className="dashboard-organization-picker">
                    <span>{gettext("Visible to")}:</span>
                    <Form.Select
                      aria-label="Organization of the reporting dashboard"
                      onChange={(e: any) => {
                        if (!changesMade) {
                          setChangesMade(true);
                        }
                        setValueHandler("organization_id")(e);
                      }}
                      value={
                        dashboardEditedValues.organization_id ??
                        dashboardOrg?.id ??
                        ""
                      }
                    >
                      <option value={0}>
                        {gettext("Global administrators")}
                      </option>
                      {organizationList?.map(
                        (organization: GetOrganizations_organization) => (
                          <option
                            key={`org_${organization.id}`}
                            value={organization.id}
                          >
                            {organization.name}
                          </option>
                        ),
                      )}
                    </Form.Select>
                  </div>
                )}
              </div>
            }
            className="mb-3"
          />

          <div className="dashboard-toolbar d-flex justify-content-end gap-2 mb-3 widget-export-button">
            {canEditDashboard && !editWidgets && (
              <Button
                as={Link as any}
                to={`/reporting-dashboards/admin/edit/${dashboard.id}`}
                variant="outline-primary"
                size="sm"
              >
                <FontAwesomeIcon icon={faPencil} className="me-2" />
                {gettext("Edit widgets")}
              </Button>
            )}
            <ButtonGroup>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleExportReport}
                disabled={isExporting}
              >
                <FontAwesomeIcon icon={faFileExport} className="me-2" />
                {gettext("Export Report")}
              </Button>
              <Dropdown
                as={ButtonGroup}
                onSelect={(key) => setExportFormat(key as ExportFormat)}
              >
                <Dropdown.Toggle
                  variant="outline-primary"
                  size="sm"
                  split
                  disabled={isExporting}
                  className="export-format-toggle"
                >
                  {EXPORT_FORMATS.find((f) => f.value === exportFormat)?.label}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  {EXPORT_FORMATS.map((format) => (
                    <Dropdown.Item
                      key={format.value}
                      eventKey={format.value}
                      active={exportFormat === format.value}
                    >
                      {format.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </ButtonGroup>
          </div>

          {canEditWidgets && stationsExist && (
            <div className="alert alert-info">
              <strong>Editing reporting dashboard</strong>
              <br />
              <small>
                Configure each widget's date range and visual settings. Changes
                are saved for all users viewing this report.
              </small>
            </div>
          )}

          {canEditWidgets && !stationsExist && (
            <div className="alert alert-info">
              <strong>Editing reporting dashboard</strong>
              <br />
              <small>
                No stations exist on this dashboard. Add a station to continue
                creating new widgets.
              </small>
            </div>
          )}

          {editedStructure?.map((stationStructure, stationIndex) => (
            <ReportingDashboardStation
              key={`reporting_station_${stationStructure.station?.identifier}`}
              station={stationStructure.station}
              stationIndex={stationIndex}
              editWidgets={canEditWidgets}
              editedStructure={editedStructure}
              setEditedStructure={setEditedStructure}
              setChangesMade={setChangesMade}
              onWidgetDateRangeChange={handleWidgetDateRangeChange}
              onOpenWidgetSettings={openWidgetSettings}
              onOpenDatasourceSettings={openDatasourceSettings}
              canEditVisualSettings={canEditWidgets}
              onExportWidget={handleExportWidget}
              onExportWidgetExcel={handleExportWidgetExcel}
              dashboardId={numericId}
              onCopyWidget={showCopyWidget}
            />
          ))}

          {canEditWidgets && (
            <Button
              variant="primary"
              size="sm"
              onClick={showAddStation}
              className="add-station-button"
            >
              {gettext("Add station")}
            </Button>
          )}

          {canEditWidgets && (
            <div className={`floating-footer ${changesMade ? "visible" : ""}`}>
              <Flex justifyContent="md-center">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={onCancelEditingStructure}
                  disabled={submitDisabled}
                >
                  {gettext("Cancel")}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onSaveEditedStructure}
                  disabled={submitDisabled}
                >
                  {gettext("Save changes")}
                </Button>
              </Flex>
            </div>
          )}
        </div>
      </DragProvider>
    </>
  );
};

export default ReportingDashboardDetailsPage;
